import React, { FunctionComponent, ReactElement } from 'react';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import { FormattedMessage, useIntl } from 'react-intl';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { formatCurrency } from '@empiriecom/module-components/FormattedCurrency';
import { FragmentConfiguration } from '@/config';
import { CalculationResult, CalculatorType } from '../../types';
import messages from './messages';

const StyledOverviewTable = styled.div<{
  cardLayout: boolean;
  isMobile: boolean;
  borderColor: string;
}>`
  display: flex;
  flex-direction: column;
  font-size: 0.8125rem;
  border-radius: 5px;
  ${({ cardLayout, isMobile, borderColor, theme }) =>
    !isMobile &&
    !cardLayout &&
    `border: .0625rem solid ${borderColor !== '' ? borderColor : theme.greyNormal.color.normal};`}
  ${({ isMobile, cardLayout }) =>
    isMobile || cardLayout
      ? 'width: 100%; min-width: 0;'
      : 'padding: 0 .875rem; width: 45%; min-width: 250px;'}
`;

const ListRow = styled.div<{
  borderColor: string;
  calculatorType: CalculatorType;
  cardLayout: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.375rem 0;
  ${({ borderColor, theme }) =>
    `border-bottom: .0625rem solid ${
      borderColor !== '' ? borderColor : theme.greyNormal.color.normal
    };`}
  &:first-of-type {
    ${({ borderColor, calculatorType, cardLayout, theme }) =>
      calculatorType === 'DYNAMIC' &&
      cardLayout &&
      `border-top: .0625rem solid ${
        borderColor !== '' ? borderColor : theme.greyNormal.color.normal
      };`}
  }
  &:last-of-type {
    border: 0px none;
  }
`;

const RowLabel = styled.div<{ cardLayout: boolean }>`
  ${({ cardLayout }) => !cardLayout && 'margin-left: 0.5rem;'}
`;

const RowValue = styled.div`
  white-space: nowrap;
`;

const parseValueToPercent = (toParse: number, locale: string): string =>
  (toParse / 100).toLocaleString(locale, {
    minimumFractionDigits: 2,
    style: 'percent',
  });

export interface OverviewTableProps {
  /* The response data. */
  response: CalculationResult;
  /** The border color string. */
  borderColor?: string;
}

export const OverviewTable: FunctionComponent<OverviewTableProps> = ({
  response,
  borderColor = '',
}): ReactElement => {
  const {
    installmentCalculator: { cardLayout },
    page: {
      values: { defaultLocale },
    },
  } = useConfig<FragmentConfiguration>();
  const intl = useIntl();
  const mobile = useMedia({ desktop: false, mobile: true, tablet: false });

  const formatFunctions = {
    label: (txt) => <RowLabel cardLayout={cardLayout}>{txt}</RowLabel>,
    value: (txt) => <RowValue>{txt}</RowValue>,
  };

  const tableRowsToDisplay: JSX.Element[] = [];
  // eslint-disable-next-line array-callback-return, consistent-return
  response.displayOrder.map((tableRow, index) => {
    if (typeof response[tableRow] !== 'undefined') {
      switch (tableRow) {
        case 'monthlyInterestRateFactor':
        case 'yearlyInterestRateFactor':
        case 'fixedMonthlyInterestFactor':
          tableRowsToDisplay.push(
            <ListRow
              key={tableRow.concat(index.toFixed())}
              borderColor={borderColor}
              calculatorType={response.type}
              cardLayout={cardLayout}
            >
              {intl.formatMessage(messages[tableRow], {
                ...formatFunctions,
                data: parseValueToPercent(response[tableRow]?.value, defaultLocale),
              })}
            </ListRow>,
          );
          break;
        case 'singleInstallment':
          tableRowsToDisplay.push(
            <ListRow
              key={tableRow.concat(index.toFixed())}
              borderColor={borderColor}
              calculatorType={response.type}
              cardLayout={cardLayout}
            >
              {response.type === 'DYNAMIC' ? (
                intl.formatMessage(messages.singleInstallmentRVC, {
                  ...formatFunctions,
                  data: formatCurrency(
                    response.currency,
                    response.singleInstallment.value * 100,
                    intl,
                  ),
                })
              ) : (
                <FormattedMessage
                  id="installment-calculator.single-installment-default"
                  defaultMessage="<label>{noOfInstallments} Folgeraten je</label><value>{data}</value>"
                  values={{
                    ...formatFunctions,
                    data: formatCurrency(
                      response.currency,
                      response.singleInstallment.value * 100,
                      intl,
                    ),
                    noOfInstallments: response.numberOfRegularInstallments,
                  }}
                />
              )}
            </ListRow>,
          );
          break;
        case 'overallPrice':
          tableRowsToDisplay.push(
            <ListRow
              key={tableRow.concat(index.toFixed())}
              borderColor={borderColor}
              calculatorType={response.type}
              cardLayout={cardLayout}
            >
              {response.type === 'DYNAMIC' ? (
                intl.formatMessage(messages.overallPriceRVC, {
                  ...formatFunctions,
                  data: formatCurrency(response.currency, response.overallPrice.value * 100, intl),
                })
              ) : (
                <>
                  <RowLabel cardLayout={cardLayout}>
                    <strong>
                      <FormattedMessage
                        id="installment-calculator.overall-price-default"
                        defaultMessage="Dein Ratenkaufpreis"
                      />
                    </strong>
                  </RowLabel>
                  <RowValue>
                    <strong>
                      {formatCurrency(response.currency, response.overallPrice.value * 100, intl)}
                    </strong>
                  </RowValue>
                </>
              )}
            </ListRow>,
          );
          break;
        default:
          tableRowsToDisplay.push(
            <ListRow
              key={tableRow.concat(index.toFixed())}
              borderColor={borderColor}
              calculatorType={response.type}
              cardLayout={cardLayout}
            >
              {intl.formatMessage(messages[tableRow], {
                ...formatFunctions,
                data: formatCurrency(response.currency, response[tableRow]?.value * 100, intl),
              })}
            </ListRow>,
          );
      }
    }
  });

  return tableRowsToDisplay.length > 0 ? (
    <StyledOverviewTable cardLayout={cardLayout} isMobile={mobile} borderColor={borderColor}>
      {tableRowsToDisplay}
    </StyledOverviewTable>
  ) : null;
};

export default OverviewTable;
